import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../../config';
import { prepareHeaders } from './utils';

const TagType = {
    MFA_SETTINGS: 'MfaSettings',
} as const;

export type OtpStatus = 'ACTIVATED' | 'DEACTIVATED';

export interface MfaSettings {
    otp: {
        status: OtpStatus;
    };
}

const transformMfaSettings = (response: unknown): MfaSettings => {
    if (
        typeof response === 'object' &&
        response !== null &&
        'otp' in response &&
        typeof response.otp === 'object' &&
        response.otp !== null &&
        'status' in response.otp &&
        isOtpStatus(response.otp.status)
    ) {
        return response as MfaSettings;
    }

    throw new Error('Invalid input for mfa settings.');
};

export const authServerApi = createApi({
    reducerPath: 'authServerApi',
    baseQuery: fetchBaseQuery({ baseUrl: config.backend.AUTH_SERVER, prepareHeaders }),
    tagTypes: [TagType.MFA_SETTINGS],
    endpoints: build => ({
        fetchSelfMfaSettings: build.query<MfaSettings, void>({
            query: () => ({ url: '/mfa-settings/self' }),
            providesTags: [TagType.MFA_SETTINGS],
            transformResponse: transformMfaSettings,
        }),

        activateOtp: build.mutation<MfaSettings, void>({
            query: () => ({
                url: '/mfa-settings/self',
                method: 'PUT',
                body: { otp: { status: 'ACTIVATED' } },
            }),
            transformResponse: transformMfaSettings,
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedSettings } = await queryFulfilled;
                    dispatch(
                        authServerApi.util.updateQueryData('fetchSelfMfaSettings', undefined, draft => {
                            Object.assign(draft, updatedSettings);
                        })
                    );
                } catch {
                    // Do nothing here! The cache wasn't touched.
                    // Let the component handle the UI feedback.
                }
            },
        }),

        deactivateOtp: build.mutation<MfaSettings, void>({
            query: () => ({
                url: '/mfa-settings/self',
                method: 'PUT',
                body: { otp: { status: 'DEACTIVATED' } },
            }),
            transformResponse: transformMfaSettings,
            async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedSettings } = await queryFulfilled;
                    dispatch(
                        authServerApi.util.updateQueryData('fetchSelfMfaSettings', undefined, draft => {
                            Object.assign(draft, updatedSettings);
                        })
                    );
                } catch {
                    // Do nothing here! The cache wasn't touched.
                    // Let the component handle the UI feedback.
                }
            },
        }),
    }),
});

export const { useFetchSelfMfaSettingsQuery, useActivateOtpMutation, useDeactivateOtpMutation } = authServerApi;

const isOtpStatus = (input: unknown): input is OtpStatus => input === 'ACTIVATED' || input === 'DEACTIVATED';
